import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { RouterLink } from '@angular/router';
import { TableColumn, TableCaption, AuditsData } from '../../../models';
import { ActionMenuComponent } from '../action-menu/action-menu.component';
import { AuditModalComponent } from '../audit-modal/audit-modal.component';
import { Dialog, DialogModule } from 'primeng/dialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../../core/services/auth.service';
import { UserRole } from '../../../models/auth.model';
import { formatDateDDMMYYYY } from '../../../utils/date.util';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TagModule,
    ActionMenuComponent,
    AuditModalComponent,
    DialogModule,
    ToggleSwitchModule,
    FormsModule,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent {
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() loading: boolean = false;
  @Input() context: string = 'default';
  @Input() isArchivedView: boolean = false;
  @Input() getPermissionForRow?: (row: any) => number; // Function to get permission for a row

  @Output() rowClick = new EventEmitter<any>();
  @Output() comment = new EventEmitter<any>();
  @Output() statusToggle = new EventEmitter<{ row: any; event: any }>();
  @Output() reply = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() auditLog = new EventEmitter<any>();
  @Output() approve = new EventEmitter<any>();
  @Output() reject = new EventEmitter<any>();
  @Output() documentNameClick = new EventEmitter<any>();

  auditData: AuditsData[] = [];
  auditDialogVisible: boolean = false;

  selectedRow: any;
  isManager: boolean = false;

  constructor(private authService: AuthService) {}

  onRowClick(row: any) {
    this.rowClick.emit(row);
  }

  onDocumentNameClick(row: any) {
    this.documentNameClick.emit(row);
  }
  ngOnInit(): void {
    this.checkUserRole();
  }

  private checkUserRole(): void {
    this.isManager = this.authService.hasRole(UserRole.MANAGER);
  }

  get shouldShowReplyButton(): boolean {
    return this.context === 'letters' && this.isManager;
  }

  get shouldShowActionMenu(): boolean {
    // Don't show action menu for archived users, letters, or routing slips
    if ((this.context === 'users' || this.context === 'letters' || this.context === 'routing-slip') && this.isArchivedView) {
      return false;
    }
    return this.context === 'letters' && !this.isManager;
  }
  onComment(row: any) {
    this.comment.emit(row);
  }

  onStatusToggle(row: any, event: any): void {
    // Emit the status change event
    this.statusToggle.emit({ row, event });
  }

  onEdit(row: any): void {
    this.edit.emit(row);
  }

  onDelete(row: any): void {
    this.delete.emit(row);
  }

  onReply(row: any): void {
    this.reply.emit(row);
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-500';
      case 'in progress':
      case 'in-progress':
        return 'bg-yellow-500';
      case 'viewed':
        return 'bg-indigo-500';
      case 'approved':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'reviewed':
        return 'bg-blue-500';
      case 'responded':
        return 'bg-green-500';
      case 'archived':
        return 'bg-gray-500';
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  }

  // Check if a column is a date column
  isDateColumn(columnKey: string): boolean {
    const dateColumns = [
      'createdOn',
      'date',
      'assignedDate',
      'dueDate',
      'updatedAt',
      'createdAt',
    ];
    return dateColumns.includes(columnKey);
  }

  // Format date using the utility function
  formatDate(dateValue: any): string {
    // If the date is already formatted (contains dashes in DD-MM-YYYY format), return as is
    if (
      typeof dateValue === 'string' &&
      /^\d{2}-\d{2}-\d{4}$/.test(dateValue)
    ) {
      return dateValue;
    }
    // Otherwise, format it using the utility function
    return formatDateDDMMYYYY(dateValue);
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Reviewed':
        return 'info';
      case 'Responded':
        return 'success';
      case 'Archived':
        return 'secondary';
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'secondary';
      default:
        return 'secondary';
    }
  }
  onView(row: any) {
  }
  onApprove(row: any) {
    this.approve.emit(row);
  }
  onReject(row: any) {
    this.reject.emit(row);
  }
  // open modal
  onAuditLog(row: any) {
    this.auditLog.emit(row);
  }

  closeAuditLog() {
    this.auditDialogVisible = false;
    this.selectedRow = null;
  }

  /**
   * Check if a value is an email address
   */
  isEmailAddress(value: any): boolean {
    if (typeof value !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  /**
   * Get the CSS class for table cell based on content
   */
  getCellClass(column: any, row: any): string {
    const value = row[column.key];
    if (this.isEmailAddress(value)) {
      return 'email-cell';
    }
    return '';
  }

  /**
   * Get the CSS class for table header based on column key
   */
  getHeaderClass(column: any): string {
    // Check if the column key suggests it contains email addresses
    const emailKeys = [
      'email',
      'emailAddress',
      'userEmail',
      'contactEmail',
      'assignedEmail',
    ];
    if (emailKeys.some((key) => column.key.toLowerCase().includes(key))) {
      return 'email-cell';
    }
    return '';
  }

  /**
   * Get the CSS class for column width based on column key and context
   */
  getColumnWidthClass(columnKey: string): string {
    // Email Address column in users context - make it wider
    if (columnKey === 'emailAddress' && this.context === 'users') {
      return 'wider-column';
    }

    // Document Name column in routing-slip context - make it wider
    if (columnKey === 'documentName' && this.context === 'routing-slip') {
      return 'wider-column';
    }

    return '';
  }
}
